import { useState, useEffect, useCallback, useRef } from 'react';
import MessagingService from '../services/MessagingService';
import { authAPI } from '../api/api';
import { toast } from 'react-toastify';

export const useMessaging = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentFolder, setCurrentFolder] = useState('inbox');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [folderStats, setFolderStats] = useState({});
    const [users, setUsers] = useState({ backend_users: [], client_users: [] });

    // Stabil referenciát használunk a MessagingService-hez
    const messagingServiceRef = useRef();
    if (!messagingServiceRef.current) {
        messagingServiceRef.current = new MessagingService();
    }
    
    // Referencia az első betöltés követésére
    const isFirstLoadRef = useRef(true);

    const loadMessages = useCallback(async (folder, pageNum = 1, search = '') => {
        try {
            setLoading(true);
            setError(null);
            
            const result = await messagingServiceRef.current.getMessages(folder, pageNum, search);
            
            if (pageNum === 1) {
                setMessages(result.messages || []);
            } else {
                setMessages(prev => [...prev, ...(result.messages || [])]);
            }
            
            setHasMore((result.messages || []).length === 20); // 20 per page
            setPage(pageNum);
            setCurrentFolder(folder);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
        } finally {
            setLoading(false);
        }
    }, []); // Nincs függőség

    const loadUsers = useCallback(async () => {
        try {
            const result = await messagingServiceRef.current.getUsers();
            setUsers(result);
        } catch (err) {
            toast.error(' useMessaging - Hiba a felhasználók betöltésekor:', err);
        }
    }, []); // Nincs függőség

    const loadFolderStats = useCallback(async () => {
        try {
            const result = await messagingServiceRef.current.getFolderStats();
            setFolderStats(result);
        } catch (err) {
            toast.error('Hiba a mappa statisztikák betöltésekor:', err);
        }
    }, []); // Nincs függőség

    const saveDraft = useCallback(async (messageData) => {
        try {
            setLoading(true);
            setError(null);
            
            const result = await messagingServiceRef.current.saveDraft(messageData);
            
            // Csak frissítjük a mappa statisztikákat, nem változtatjuk meg a jelenlegi mappát
            await loadFolderStats();
            
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [loadFolderStats]);

    const updateDraft = useCallback(async (draftId, messageData) => {
        try {
            setLoading(true);
            setError(null);
            
            const result = await messagingServiceRef.current.updateDraft(draftId, messageData);
            
            // Csak frissítjük a mappa statisztikákat, nem változtatjuk meg a jelenlegi mappát
            await loadFolderStats();
            
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [loadFolderStats]);

    const sendMessage = useCallback(async (messageData) => {
        try {
            setLoading(true);
            setError(null);
            
            const result = await messagingServiceRef.current.sendMessage(messageData);
            
            // Frissítjük az üzeneteket és a mappa statisztikákat
            await loadMessages(currentFolder, 1);
            await loadFolderStats();
            
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentFolder, loadMessages, loadFolderStats]);

    const markAsRead = useCallback(async (id) => {
        try {
            await messagingServiceRef.current.markAsRead(id);
            
            // Frissítjük a helyi állapotot
            setMessages(prev => prev.map(msg => 
                msg.id === id ? { ...msg, read: true } : msg
            ));
            
            // Frissítjük a mappa statisztikákat is
            await loadFolderStats();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
        }
    }, [loadFolderStats]);

    const deleteMessage = useCallback(async (id) => {
        try {
            await messagingServiceRef.current.deleteMessage(id);
            
            // A deleteMessage most move_to_trash műveletet használ, így az üzenet a kuka mappába kerül
            // Helyileg eltávolítjuk az üzenetet az aktuális mappából
            setMessages(prev => prev.filter(msg => msg.id !== id));
            
            // Frissítjük a mappa statisztikákat is
            await loadFolderStats();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
        }
    }, [loadFolderStats]);

    const bulkActions = useCallback(async (action, messageIds) => {
        try {
            await messagingServiceRef.current.bulkActions(action, messageIds);
            
            if (action === 'delete' && currentFolder === 'trash') {
                // Ha a kuka mappából törölünk, akkor végleges törlés - helyileg eltávolítjuk
                setMessages(prev => prev.filter(msg => !messageIds.includes(msg.id)));
                await loadFolderStats();
            } else if (action === 'move_to_trash') {
                // Ha a kuka mappába helyezünk át, akkor helyileg eltávolítjuk az üzeneteket
                setMessages(prev => prev.filter(msg => !messageIds.includes(msg.id)));
                await loadFolderStats();
            } else {
                // Egyéb esetekben frissítjük az üzeneteket és a mappa statisztikákat
                await loadMessages(currentFolder, 1);
                await loadFolderStats();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
        }
    }, [currentFolder, loadMessages, loadFolderStats]);

    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            loadMessages(currentFolder, page + 1);
            
            // Frissítjük a mappa statisztikákat is
            loadFolderStats();
        }
    }, [loading, hasMore, currentFolder, page, loadMessages, loadFolderStats]);

    const uploadAttachment = useCallback(async (file, messageId) => {
        try {
            const result = await messagingServiceRef.current.uploadAttachment(file, messageId);
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        }
    }, []);

    const getMessageAttachments = useCallback(async (messageId) => {
        try {
            const result = await messagingServiceRef.current.getMessageAttachments(messageId);
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        }
    }, []);

    const toggleFavorite = useCallback(async (messageId) => {
        try {
            const result = await messagingServiceRef.current.toggleFavorite(messageId);
            
            // Frissítjük a helyi állapotot
            setMessages(prev => prev.map(msg => 
                msg.id === messageId ? { ...msg, is_favorite: !msg.is_favorite } : msg
            ));
            
            // Frissítjük a mappa statisztikákat is
            await loadFolderStats();
            
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Hiba történt');
            throw err;
        }
    }, [loadFolderStats]);


    // Csak egyszer fut le a komponens betöltésekor
    useEffect(() => {
        // Client user ID beállítása a bejelentkezett userből
        const currentUserId = authAPI.getCurrentUserId();

        
        if (currentUserId) {
            messagingServiceRef.current.setClientUserId(currentUserId);
        } else {
            toast.warn('⚠️ useMessaging - Nincs bejelentkezett user');
            // Fallback - ne állítsunk be semmit, hagyni kell hogy a backend hibát dobjon
        }
        
        // Csak az első betöltéskor töltjük be az inbox-ot
        if (isFirstLoadRef.current) {
            loadMessages('inbox');
            isFirstLoadRef.current = false;
        }
        loadUsers();
        loadFolderStats();
    }, []); // Üres függőség tömb

    return {
        messages,
        loading,
        error,
        currentFolder,
        hasMore,
        folderStats,
        users,
        loadMessages,
        saveDraft,
        updateDraft,
        sendMessage,
        markAsRead,
        deleteMessage,
        bulkActions,
        loadMore,
        uploadAttachment,
        getMessageAttachments,
        loadFolderStats,
        toggleFavorite,
        setCurrentFolder,
        setError
    };
};
